import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeftIcon, SquarePenIcon } from "lucide-react";
import { AiDisclaimer } from "@/components/ai-documents/ai-disclaimer";
import { BlankDocumentEditor } from "@/components/ai-documents/blank-document-editor";
import { CustomToolRunner } from "@/components/ai-documents/custom-tool-runner";
import { DocumentGenerationGate } from "@/components/ai-documents/document-generation-progress";
import { DocumentGenerator } from "@/components/ai-documents/document-generator";
import { Link } from "@/core/lib/navigation";
import { useSession } from "@/core/lib/session-context";
import { buildDocumentDefaults } from "@/core/schema/document-schema";
import { usePermissions } from "@/hooks/use-permissions";
import { documentFormHref } from "@/lib/document-links";
import { companyQuery, documentQuery, profileQuery } from "@/lib/loaders";
import { Button } from "@/ui/components/ui/button";

/**
 * The dedicated view for a single generated document, reached at
 * `/document/{id}` from anywhere a saved document is opened. It resolves the
 * document in the loader to learn its type (built-in vs a custom tool) and
 * mounts the same editor the generator uses — but lands straight on the
 * generated text, with no wizard chrome. A draft with no output yet is opened
 * through the input form instead (callers route those to `/generate/...`), so
 * this route assumes a real document and 404s when the id doesn't resolve.
 * "Edit form" links back to the wizard.
 */
export const Route = createFileRoute("/_workspace/document/$id")({
  loader: async ({ context, params }) => {
    const [doc] = await Promise.all([
      context.queryClient.ensureQueryData(documentQuery(params.id)),
      context.queryClient.ensureQueryData(companyQuery),
      context.queryClient.ensureQueryData(profileQuery),
    ]);
    if (!doc) throw notFound();
  },
  component: DocumentPage,
});

function DocumentPage() {
  const { id } = Route.useParams();
  const { data: doc } = useQuery(documentQuery(id));
  const { data: company } = useQuery(companyQuery);
  const { data: profile } = useQuery(profileQuery);
  const session = useSession();
  const canEdit = usePermissions().can("documents", "edit");

  if (!doc) return null;

  const editFormHref = documentFormHref(doc, "form");

  // While the async generation is still running (or after it failed), the row
  // has no output yet — gate the editor behind the live progress screen.
  const generationPending = doc.status === "generating" || doc.status === "generation_failed";

  // The editor is computed regardless of generation state so the progress gate
  // can swap it in the instant generation completes. Its props don't depend on
  // the generated output (the editor fetches that itself), so they're safe to
  // resolve while still generating.
  const editor =
    doc.kind === "blank" ? (
      <BlankDocumentEditor
        company={company ?? null}
        user={{ name: session.user.name, email: session.user.email }}
        seedDocId={doc.id}
        savedSignature={profile?.signature ?? ""}
        defaultSignerName={session.user.name || company?.issuerName || profile?.displayName || ""}
      />
    ) : doc.kind === "custom" ? (
      <CustomToolRunner
        toolId={doc.documentType}
        company={company ?? null}
        user={{ name: session.user.name, email: session.user.email }}
        seedDocId={doc.id}
        savedSignature={profile?.signature ?? ""}
        defaultSignerName={session.user.name || company?.issuerName || profile?.displayName || ""}
      />
    ) : (
      <DocumentGenerator
        defaults={buildDocumentDefaults({
          documentType: doc.documentType,
          company: company ?? null,
          profile: profile ?? null,
        })}
        seed={{ mode: "doc", id: doc.id, view: "editor" }}
        savedSignature={profile?.signature ?? ""}
        company={company ?? null}
        typeName={doc.typeName}
      />
    );

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-3">
            <Link
              href="/documents"
              className="inline-flex items-center gap-1.5 text-muted-foreground text-sm underline-offset-4 hover:underline"
            >
              <ArrowLeftIcon className="size-4" />
              Back to Documents
            </Link>
            <div className="space-y-1">
              <h1 className="font-bold text-2xl tracking-tight">{doc.name}</h1>
              <p className="text-muted-foreground text-sm">{doc.typeName}</p>
            </div>
          </div>
          {doc.status !== "generating" && canEdit && (
            <Button asChild variant="outline" className="gap-1.5">
              <Link href={editFormHref}>
                <SquarePenIcon className="size-4" />
                Edit form
              </Link>
            </Button>
          )}
        </div>
        {generationPending ? (
          <DocumentGenerationGate
            id={doc.id}
            name={doc.name}
            typeName={doc.typeName}
            initialStatus={doc.status}
            run={doc.updatedAt}
          >
            {editor}
          </DocumentGenerationGate>
        ) : (
          editor
        )}
        {/* Template and blank documents are filled from what the user wrote
            rather than by AI, so they carry no AI disclaimer. */}
        {doc.kind !== "custom" && doc.kind !== "blank" && <AiDisclaimer />}
      </div>
    </main>
  );
}
